import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Smartphone, Plus, X } from 'lucide-react';
import { useAppCategories } from '@/hooks/useAppCategories';
import { useAppRestrictions } from '@/hooks/useAppRestrictions';

const Apps: React.FC = () => {
  const navigate = useNavigate();
  const { categories, isLoading: categoriesLoading, hasDevice } = useAppCategories();
  const { 
    restrictions, 
    updateCategoryRestriction, 
    addToWhitelist, 
    removeFromWhitelist,
    addToBlacklist,
    removeFromBlacklist,
    isLoading: restrictionsLoading 
  } = useAppRestrictions();

  const [newWhitelistApp, setNewWhitelistApp] = useState('');
  const [newBlacklistApp, setNewBlacklistApp] = useState('');

  const handleAddWhitelist = async () => {
    if (newWhitelistApp.trim()) {
      try {
        await addToWhitelist(newWhitelistApp.trim());
        setNewWhitelistApp('');
      } catch (error) {
        console.error('Failed to add to whitelist:', error);
      }
    }
  };

  const handleAddBlacklist = async () => {
    if (newBlacklistApp.trim()) {
      try {
        await addToBlacklist(newBlacklistApp.trim());
        setNewBlacklistApp('');
      } catch (error) {
        console.error('Failed to add to blacklist:', error);
      }
    }
  };

  const isLoading = categoriesLoading || restrictionsLoading;

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-background border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/settings')}
              className="p-1.5"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-lg font-bold text-foreground">Categories</h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-4">

        {/* Device Selection Notice */}
        {!hasDevice && (
          <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950 dark:border-amber-800">
            <p className="text-amber-800 dark:text-amber-200 text-sm">
              Please select a device from the Device Selector to view categories.
            </p>
          </div>
        )}

        {/* Categories Table */}
        {isLoading ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground text-sm">Loading categories...</p>
          </div>
        ) : !hasDevice ? (
          <div className="text-center py-8">
            <Smartphone className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground text-sm">No device selected</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-sm">Category</TableHead>
                <TableHead className="text-sm">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
               {categories?.map((category) => {
                 const { name, isAllowed, status, source } = category;
                 
                 let rowBgClass = '';
                 let statusColorClass = '';
                 let statusText = '';
                 
                 if (status === 'blocked') {
                   rowBgClass = 'bg-red-50 hover:bg-red-100 dark:bg-red-950/20 dark:hover:bg-red-950/30';
                   statusColorClass = 'text-red-700 dark:text-red-400';
                   statusText = 'Blocked';
                 } else if (status === 'allowed') {
                   rowBgClass = 'bg-green-50 hover:bg-green-100 dark:bg-green-950/20 dark:hover:bg-green-950/30';
                   statusColorClass = 'text-green-700 dark:text-green-400';
                   statusText = 'Allowed';
                 } else if (status === 'allowed_with_rules') {
                   rowBgClass = 'bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/20 dark:hover:bg-blue-950/30';
                   statusColorClass = 'text-blue-700 dark:text-blue-400';
                   statusText = 'Allowed with rules';
                 }
                 
                 return (
                   <TableRow 
                     key={name} 
                     className={rowBgClass}
                   >
                     <TableCell className="p-3">
                       <div>
                         <div className="flex items-center gap-2">
                           <span className="font-medium text-sm">{name}</span>
                           {source && (
                             <Badge variant="secondary" className="text-xs">
                               {source === 'device' ? 'Device' : 'Level'}
                             </Badge>
                           )}
                         </div>
                         {category.tags && category.tags.length > 0 && (
                           <p className="text-xs text-muted-foreground">{category.tags.join(', ')}</p>
                         )}
                       </div>
                     </TableCell>
                     <TableCell className="p-3">
                       <span className={`font-medium text-sm ${statusColorClass}`}>
                         {statusText}
                       </span>
                     </TableCell>
                   </TableRow>
                 );
               })}
            </TableBody>
          </Table>
        )}

      </div>
    </div>
  );
};

export default Apps;